import { useState, type CSSProperties } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useNav } from '@/shared/hooks/use-nav'
import { Bell, ChevronDown, MapPin, Plus, Search, X } from 'lucide-react'
import { TopBar } from '@/shared/components/top-bar'
import { LocationBar } from '@/shared/components/location-bar'
import { IconButton } from '@/shared/ui/icon-button'
import { SearchField } from '@/shared/ui/search-field'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { Chip } from '@/shared/ui/chip'
import { Button } from '@/shared/ui/button'
import { MatchCard } from '@/shared/components/match-card'
import { MatchCardSkeleton } from '@/shared/components/match-card-skeleton'
import { EmptyState } from '@/shared/ui/empty-state'
import { ErrorState } from '@/shared/ui/error-state'
import { LEVEL_BANDS } from '@/shared/levels'
import { usePartidos, type PositionFilter, type TimeWindow, type TypeFilter } from './use-partidos'
import { usePendingInvites } from './use-match-invites'

const TIMES: { value: TimeWindow; label: string }[] = [
  { value: 'hoy', label: 'Hoy' },
  { value: 'manana', label: 'Mañana' },
  { value: 'semana', label: 'Esta semana' },
]
const TYPES: { value: Exclude<TypeFilter, 'todos'>; label: string }[] = [
  { value: 'Casual', label: 'Casual' },
  { value: 'Competitivo', label: 'Competitivo' },
]

// Discovery feed — location-first: the feed is centred on a place + radius, then
// narrowed by time window and match type. Reveal-on-tap search + the 4 states.
// Static mock; swaps to a Firestore geohash query later with the same handling.
export function PartidosPage() {
  const navigate = useNav()
  // Deep link from club detail: /partidos?lugar=<club> pre-fills the search
  // with the club name so the feed opens already narrowed to that place.
  const [searchParams] = useSearchParams()
  const initialLugar = searchParams.get('lugar') ?? ''
  const feed = usePartidos(initialLugar)
  const { status, matches, query, setQuery, timeWindow, setTimeWindow, typeFilter, setTypeFilter } = feed
  const [searching, setSearching] = useState(!!initialLugar)
  const [levelSheet, setLevelSheet] = useState(false)
  const [positionSheet, setPositionSheet] = useState(false)
  const [genderSheet, setGenderSheet] = useState(false)
  const [timeSheet, setTimeSheet] = useState(false)
  const [typeSheet, setTypeSheet] = useState(false)


  // An AUTO-derived level band is not an active filter: the player never set
  // it, so offering "Quitar filtros" would blame them for a choice they did not
  // make — and it put a second "Crear partido" button on screen next to the nav
  // FAB. The empty state below offers the honest escape instead.
  const filtersActive =
    timeWindow !== 'todos' ||
    typeFilter !== 'todos' ||
    (feed.levelBand !== null && !feed.levelBandAuto) ||
    feed.position !== 'todos' ||
    feed.gender !== 'todos'

  function clearFilters() {
    setTimeWindow('todos')
    setTypeFilter('todos')
    feed.setLevelBand(null)
    feed.setPosition('todos')
    feed.setGender('todos')
  }

  return (
    <>
      <TopBar
        title="Partidos"
        actions={
          <>
            <ActivityBell />
            <IconButton
              label={searching ? 'Cerrar búsqueda' : 'Buscar'}
              onClick={() => {
                setSearching((v) => !v)
                if (searching) setQuery('')
              }}
            >
              {searching ? <X size={20} /> : <Search size={20} />}
            </IconButton>
          </>
        }
      />

      <div className="border-b border-[var(--chrome-line)]">
        <LocationBar location={feed.location} onLocationChange={feed.setLocation} />
      </div>

      {searching && (
        <div className="px-4 pt-3 um-fade">
          <SearchField autoFocus value={query} onChange={setQuery} placeholder="Buscar por club o ciudad" />
        </div>
      )}

      {/* shrink-0 on the wrapper: overflow-x-auto zeroes the automatic
          min-height, letting the scroll-column squish this row to its padding
          (clipped chips). Nivel + Posición lead (the seeker's top filters);
          the edge fades below hint at what's still off-screen. */}
      <div className="relative shrink-0">
        {/* WRAPS, no longer scrolls horizontally (2026-08-08). The row measured
            850px of chips in a 390px viewport: 5 of 8 controls — including
            Hoy/Mañana/Esta semana and Casual/Competitivo — started off-screen
            behind a fade most players never scrolled. A filter you cannot see
            is a filter you do not use, and these were the seeker's fastest ones.
            Wrapping alone made all 8 visible but cost 184px — 22% of the
            viewport before a single match card. So time and type now open
            SHEETS, exactly as Nivel/Posición/Quién juega already did: 8 controls
            become 5, one consistent interaction, no horizontal scroll, and the
            chip shows the active value instead of hiding it off-screen. */}
        <div key={feed.location} className="flex flex-wrap gap-2 px-4 py-3">
          {feed.homeClubId && (
            <Chip active={feed.onlyMyClub} onClick={() => feed.setOnlyMyClub(!feed.onlyMyClub)}>
              Mi club
            </Chip>
          )}
          <Chip active={feed.levelBand !== null} onClick={() => setLevelSheet(true)}>
            {feed.levelBand ? `Nivel ${feed.levelBand.label}` : 'Nivel'} <ChevronDown size={14} />
          </Chip>
          <Chip active={feed.position !== 'todos'} onClick={() => setPositionSheet(true)}>
            {/* "Posición: Revés", mirroring "Nivel Medio" — a bare "Revés" made the
                active chip stop naming its own axis, unlike every sibling. */}
            {feed.position !== 'todos' ? `Posición: ${feed.position}` : 'Posición'}{' '}
            <ChevronDown size={14} />
          </Chip>
          <Chip active={feed.gender !== 'todos'} onClick={() => setGenderSheet(true)}>
            {/* "Jugadores" read as a player-COUNT filter; it is the gender
                category. Crear calls the same axis "¿Quién puede unirse?" —
                one concept must not wear two names across two screens. */}
            {feed.gender === 'women' ? 'Femenino' : feed.gender === 'men' ? 'Masculino' : 'Quién juega'} <ChevronDown size={14} />
          </Chip>
          <Chip active={timeWindow !== 'todos'} onClick={() => setTimeSheet(true)}>
            {TIMES.find((t) => t.value === timeWindow)?.label ?? 'Cuándo'} <ChevronDown size={14} />
          </Chip>
          <Chip active={typeFilter !== 'todos'} onClick={() => setTypeSheet(true)}>
            {typeFilter !== 'todos' ? typeFilter : 'Tipo'} <ChevronDown size={14} />
          </Chip>
        </div>
      </div>


      <BottomSheet open={levelSheet} onClose={() => setLevelSheet(false)} title="Nivel">
        <div className="flex flex-wrap gap-2 pb-2">
          <Chip
            active={feed.levelBand === null}
            onClick={() => {
              feed.setLevelBand(null)
              setLevelSheet(false)
            }}
          >
            Indiferente
          </Chip>
          {LEVEL_BANDS.map((b) => (
            <Chip
              key={b.key}
              active={feed.levelBand?.key === b.key}
              onClick={() => {
                feed.setLevelBand(b)
                setLevelSheet(false)
              }}
            >
              {b.label}
            </Chip>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet open={genderSheet} onClose={() => setGenderSheet(false)} title="Quién juega">
        <div className="flex flex-wrap gap-2 pb-2">
          {([
            ['todos', 'Cualquiera'],
            // Mixto was missing until 2026-08-12: a player who specifically
            // wants a mixed game could not filter for one, so it hid inside
            // "Cualquiera" with everything else. A category you can create and
            // cannot search for is half a feature.
            ['mixed', 'Mixto'],
            ['women', 'Femenino'],
            ['men', 'Masculino'],
          ] as const).map(([value, label]) => (
            <Chip
              key={value}
              active={feed.gender === value}
              onClick={() => {
                feed.setGender(value)
                setGenderSheet(false)
              }}
            >
              {label}
            </Chip>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet open={timeSheet} onClose={() => setTimeSheet(false)} title="Cuándo">
        <div className="flex flex-wrap gap-2 pb-2">
          <Chip
            active={timeWindow === 'todos'}
            onClick={() => {
              setTimeWindow('todos')
              setTimeSheet(false)
            }}
          >
            Cualquier día
          </Chip>
          {TIMES.map((t) => (
            <Chip
              key={t.value}
              active={timeWindow === t.value}
              onClick={() => {
                setTimeWindow(t.value)
                setTimeSheet(false)
              }}
            >
              {t.label}
            </Chip>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet open={typeSheet} onClose={() => setTypeSheet(false)} title="Tipo de partido">
        <div className="flex flex-wrap gap-2 pb-2">
          <Chip
            active={typeFilter === 'todos'}
            onClick={() => {
              setTypeFilter('todos')
              setTypeSheet(false)
            }}
          >
            Cualquiera
          </Chip>
          {TYPES.map((t) => (
            <Chip
              key={t.value}
              active={typeFilter === t.value}
              onClick={() => {
                setTypeFilter(t.value)
                setTypeSheet(false)
              }}
            >
              {t.label}
            </Chip>
          ))}
        </div>
      </BottomSheet>

      <BottomSheet open={positionSheet} onClose={() => setPositionSheet(false)} title="Posición">
        <div className="flex flex-wrap gap-2 pb-2">
          {(['todos', 'Derecha', 'Revés'] as const).map((p: PositionFilter) => (
            <Chip
              key={p}
              active={feed.position === p}
              onClick={() => {
                feed.setPosition(p)
                setPositionSheet(false)
              }}
            >
              {p === 'todos' ? 'Cualquiera' : p}
            </Chip>
          ))}
        </div>
      </BottomSheet>

      {status === 'loading' && (
        <div className="flex flex-col gap-3 px-4 pb-6" aria-busy>
          {[0, 1, 2].map((i) => <MatchCardSkeleton key={i} />)}
        </div>
      )}

      {status === 'error' && <ErrorState onRetry={feed.retry} />}

      {status === 'empty' && (
        <EmptyState
          title={
            feed.hiddenByBlock > 0
              ? 'Los partidos que coinciden son de un organizador que bloqueaste.'
              : query
                ? // Say WHY, not just "nothing matched". A search combined with
                  // the auto-derived level band used to report "Nada coincide
                  // con Maristas Padel" when that club DID have an open match —
                  // hidden by a band the player never set and that this state
                  // never mentioned, because the query branch was checked first.
                  feed.levelBandAuto
                  ? `Nada coincide con "${query}" en tu nivel.`
                  : `Nada coincide con "${query}".`
                : 'No hay partidos con estos filtros cerca.'
          }
          cta={
            query ? (
              <div className="flex flex-col items-center gap-2">
                {/* The level escape has to be offered HERE too. Arriving from a
                    club's "Ver partidos aquí" sets a query, and the auto band is
                    ANDed with it silently — so the one control that would reveal
                    the club's matches was unreachable in exactly the state that
                    needed it. */}
                {feed.levelBandAuto && (
                  <>
                    <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                      Te mostramos partidos de tu nivel ({feed.levelBand?.label}).
                    </p>
                    <Button variant="outline" size="pill" onClick={() => feed.setLevelBand(null)}>
                      Ver todos los niveles
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  size="pill"
                  onClick={() => {
                    setQuery('')
                    setSearching(false)
                  }}
                >
                  <X size={18} /> Limpiar búsqueda
                </Button>
              </div>
            ) : filtersActive ? (
              // The emptiness is filter-driven: recovery first, creation second.
              <div className="flex flex-col items-center gap-2">
                <Button variant="outline" size="pill" onClick={clearFilters}>
                  <X size={18} /> Quitar filtros
                </Button>
                <Button variant="outline" size="pill" onClick={() => navigate('/crear')}>
                  <Plus size={18} /> Crear partido
                </Button>
              </div>
            ) : feed.location !== 'Granada' ? (
              // Location-caused emptiness: offer the zone fix before creation.
              // The `|| feed.radiusKm < 10` half of this condition was dead once
              // the radius control was removed (it defaults to 10 and nothing
              // can change it), and the button advertised "· 10 km" — a radius
              // the player cannot set and that does nothing on real data. That
              // is the same honesty defect the removal was meant to close.
              <div className="flex flex-col items-center gap-2">
                <Button
                  variant="outline"
                  size="pill"
                  onClick={() => feed.setLocation('Granada')}
                >
                  <MapPin size={18} /> Ver partidos en Granada
                </Button>
                <Button variant="outline" size="pill" onClick={() => navigate('/crear')}>
                  <Plus size={18} /> Crear partido
                </Button>
              </div>
            ) : feed.levelBandAuto ? (
              // Only the derived band is narrowing this — name it, and offer to
              // widen rather than implying the player filtered themselves out.
              <div className="flex flex-col items-center gap-2">
                <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                  Te mostramos partidos de tu nivel ({feed.levelBand?.label}).
                </p>
                <Button variant="outline" size="pill" onClick={() => navigate('/crear')}>
                  <Plus size={18} /> Crear el primero
                </Button>
                <Button variant="ghost" size="md" onClick={() => feed.setLevelBand(null)}>
                  Ver todos los niveles
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="pill" onClick={() => navigate('/crear')}>
                <Plus size={18} /> Crear el primero
              </Button>
            )
          }
        />
      )}

      {status === 'loaded' && (
        <div className="flex flex-col gap-3 px-4 pb-6">
          {matches.map((m, i) => (
            <div key={m.id} className="um-enter" style={{ '--i': i } as CSSProperties}>
              <MatchCard match={m} />
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// Feed bell (decision 2026-07-06): badge = pending invitations; opens the
// Actividad screen (full history). The strip below covers act-now.
function ActivityBell() {
  const navigate = useNav()
  const invites = usePendingInvites()
  const count = invites.data?.length ?? 0
  return (
    <IconButton
      label={count > 0 ? `Actividad — ${count} ${count === 1 ? 'pendiente' : 'pendientes'}` : 'Actividad'}
      onClick={() => navigate('/actividad')}
    >
      <span className="relative inline-flex">
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-0.5 rounded-full bg-[var(--accent)] text-[color:var(--accent-fg)] text-[10px] font-bold inline-flex items-center justify-center">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </span>
    </IconButton>
  )
}

// Invitations do NOT appear in this feed (decision 2026-08-06, superseding the
// 2026-07-06 two-tier design). Partidos is the DISCOVERY surface — "one screen =
// one job" — and a personal notification is a different job wearing the most
// expensive space in the app. It was also announced twice at once: the bell said
// "1 pendiente" while the strip below repeated the same invite in full.
//
// Invitations now live in ONE place, Actividad, which already renders them with
// the identical Ver + descartar actions PLUS the accepted/rejected history the
// strip never had — so nothing was lost by removing it. `ActivityBell` keeps its
// COUNT badge (not a dot): it is now the only signal that an invite exists, and
// under-selling it is the one way this change could cost a real invite.
